import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../styles/painelOperador.css';
import '../styles/docBpoRh.css';
import { GRUPOS_DOC, MENUS_DOC } from '../data/documentacaoBpoRh';

// BPO RH → Documentação: manual passo a passo de cada menu, para a operadora
// que está chegando. O conteúdo vive em data/documentacaoBpoRh.js — ao criar ou
// mudar um menu, atualize lá (uma entrada por menu, com a rota da tela).

const normalizar = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const textoDoMenu = (m) => normalizar([
  m.nome, m.serve, m.quando, ...(m.passos || []), ...(m.regras || []), ...(m.dicas || []),
].join(' '));

function Secao({ icone, titulo, children, tom }) {
  return (
    <section className={`doc-secao ${tom || ''}`}>
      <h4><i className={`fas ${icone}`} aria-hidden="true"></i>{titulo}</h4>
      {children}
    </section>
  );
}

export default function BpoRhDocumentacao() {
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState('');
  const ativo = params.get('menu') || MENUS_DOC[0].id;
  const menu = MENUS_DOC.find((m) => m.id === ativo) || MENUS_DOC[0];

  const filtrados = useMemo(() => {
    const q = normalizar(busca.trim());
    return q ? MENUS_DOC.filter((m) => textoDoMenu(m).includes(q)) : MENUS_DOC;
  }, [busca]);

  const abrir = (id) => setParams({ menu: id });
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [ativo]);

  const idx = MENUS_DOC.findIndex((m) => m.id === menu.id);
  const anterior = MENUS_DOC[idx - 1];
  const proximo = MENUS_DOC[idx + 1];

  return (
    <div className="painel-op doc-bporh">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-book-open header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">BPO RH — Documentação</h2>
          </div>
          <div className="doc-busca">
            <i className="fas fa-magnifying-glass" aria-hidden="true"></i>
            <input type="search" placeholder="Buscar no manual (ex.: boleto, prévia, PIS)"
              value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar no manual" />
          </div>
        </div>

        <div className="doc-layout">
          <nav className="doc-nav" aria-label="Menus do BPO RH">
            {GRUPOS_DOC.map((g) => {
              const itens = filtrados.filter((m) => m.grupo === g.id);
              if (!itens.length) return null;
              return (
                <div key={g.id} className="doc-grupo">
                  <div className="doc-grupo-titulo">{g.nome}</div>
                  {itens.map((m) => (
                    <button key={m.id} type="button" onClick={() => abrir(m.id)}
                      className={`doc-aba ${m.id === menu.id ? 'ativa' : ''}`}
                      aria-current={m.id === menu.id ? 'page' : undefined}>
                      <i className={`fas ${m.icone}`} aria-hidden="true"></i>
                      <span>{m.nome}</span>
                    </button>
                  ))}
                </div>
              );
            })}
            {!filtrados.length && <div className="text-muted small p-2">Nada encontrado para “{busca}”.</div>}
          </nav>

          <article className="doc-conteudo" key={menu.id}>
            <header className="doc-cabeca">
              <div>
                <div className="doc-kicker">{(GRUPOS_DOC.find((g) => g.id === menu.grupo) || {}).nome}</div>
                <h3><i className={`fas ${menu.icone}`} aria-hidden="true"></i>{menu.nome}</h3>
                <p className="doc-serve">{menu.serve}</p>
              </div>
              {menu.rota && (
                <Link to={menu.rota} className="doc-abrir">
                  <i className="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>Abrir a tela
                </Link>
              )}
            </header>

            {menu.quando && (
              <div className="doc-quando">
                <i className="fas fa-calendar-day" aria-hidden="true"></i>
                <div><b>Quando usar:</b> {menu.quando}</div>
              </div>
            )}

            {menu.passos?.length > 0 && (
              <Secao icone="fa-list-ol" titulo="Passo a passo">
                <ol className="doc-passos">
                  {menu.passos.map((p, i) => <li key={i}>{p}</li>)}
                </ol>
              </Secao>
            )}

            {menu.regras?.length > 0 && (
              <Secao icone="fa-shield-halved" titulo="Regras e bloqueios" tom="alerta">
                <ul>{menu.regras.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </Secao>
            )}

            {menu.dicas?.length > 0 && (
              <Secao icone="fa-lightbulb" titulo="Dicas e erros comuns" tom="dica">
                <ul>{menu.dicas.map((d, i) => <li key={i}>{d}</li>)}</ul>
              </Secao>
            )}

            <footer className="doc-rodape">
              {anterior ? (
                <button type="button" className="doc-nav-btn" onClick={() => abrir(anterior.id)}>
                  <i className="fas fa-chevron-left" aria-hidden="true"></i>{anterior.nome}
                </button>
              ) : <span />}
              {proximo && (
                <button type="button" className="doc-nav-btn" onClick={() => abrir(proximo.id)}>
                  {proximo.nome}<i className="fas fa-chevron-right" aria-hidden="true"></i>
                </button>
              )}
            </footer>
          </article>
        </div>
      </div>
    </div>
  );
}
